import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  signal,
  computed,
  effect,
  untracked,
  inject,
  Injector,
} from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { toSignal } from "@angular/core/rxjs-interop";
import { IClippingStudioGraphicsRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-graphics-repository";
import { IClippingStudioMamCatalogService } from "app/js/src/domain/clipping-studio/i-clipping-studio-mam-catalog.service";
import { ClippingStudioGraphicCatalogItem } from "app/js/src/domain/clipping-studio/clipping-studio-graphic-catalog";
import { ClippingStudioMamItem } from "app/js/src/domain/clipping-studio/clipping-studio-mam-item";
import {
  ClippingStudioGraphicEvent,
  type GraphicType,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import {
  GraphicsInfo,
  GraphicsInfoColorField,
  GraphicsInfoPositionField,
  GraphicsInfoScaleField,
  GraphicsInfoTextField,
} from "app/js/src/domain/graphics-info";
import { GraphicsRepository } from "app/js/src/domain/graphics-repository";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import { ClippingStudioGfxManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-gfx-manager.service";
import {
  createInitialClippingStudioState,
  type ClippingStudioState,
} from "app/js/src/domain/clipping-studio/clipping-studio-state";

export interface AddGfxToTimelineRequest {
  gfxInstanceId: number;
  gfxParams: Record<string, string>;
  durationMs: number;
}

export interface UpdateTimelineGfxParamsRequest {
  timelineGfxId: string;
  gfxInstanceId: number;
  gfxParams: Record<string, string>;
  /**
   * Built from merged template + segment gfxParams when editor finished loading.
   * Compared on Save against {@link gfxParams} so live preview updates do not mask real edits.
   */
  baselineGfxParams: Record<string, string>;
  /** Segment bounds on editor open (edit timeline ms); used for timeline duration + SQZ revert. */
  baselineInMs: number;
  baselineOutMs: number;
  /** Host invokes once Save handling finishes (`success` = persisted/reloaded without blocked SQZ overlap). */
  onComplete?: (success: boolean) => void;
}

const USER_FACING_ERROR_MESSAGE =
  "Something went wrong. Please try again later.";

@Component({
  selector: "evrideo-clipping-studio-graphics-panel",
  templateUrl: "./clipping-studio-graphics-panel.component.html",
  styleUrls: ["./clipping-studio-graphics-panel.component.scss"],
})
export class ClippingStudioGraphicsPanelComponent implements OnInit, OnDestroy {
  @Output() addGfxToTimeline = new EventEmitter<AddGfxToTimelineRequest>();
  @Output() updateTimelineGfxParamsRequested = new EventEmitter<UpdateTimelineGfxParamsRequest>();
  readonly searchTerm = signal("");
  readonly filteredItems = signal<ClippingStudioGraphicCatalogItem[]>([]);
  readonly elementsCatalogLoading = signal(false);
  readonly elementsCatalogError = signal<string | null>(null);

  readonly sourceTab = signal<"elements" | "mam">("elements");
  readonly mamSearchTerm = signal("");
  readonly mamItems = signal<ClippingStudioMamItem[]>([]);
  readonly mamLoading = signal(false);
  readonly selectedMamInstanceForEdit = signal<ClippingStudioMamItem | null>(
    null,
  );
  readonly selectedMamInstanceGraphicsInfo = signal<GraphicsInfo | null>(null);
  readonly mamInstanceEditorLoading = signal(false);
  readonly mamInstanceEditorError = signal<string | null>(null);
  readonly mamInstanceMaterializeLoading = signal(false);
  readonly mamInstanceMaterializeReady = signal(false);

  /** GFX segment selected on the timeline: load template params + materialize for preview. */
  readonly timelineGfxGraphicsInfo = signal<GraphicsInfo | null>(null);
  readonly timelineGfxEditorLoading = signal(false);
  readonly timelineGfxEditorError = signal<string | null>(null);
  readonly timelineGfxMaterializeLoading = signal(false);
  readonly timelineGfxMaterializeReady = signal(false);

  currentMamGraphicsTemplateIndex: number | null = null;

  private timelineGfxLoadSeq = 0;
  private loadedTimelineGfxId: string | null = null;
  /** Snapshot of buildGfxParamsFromGraphicsInfo(merged) when timeline GFX editor opened; cleared on reset. */
  private timelineGfxEditorBaselineGfxParams: Record<string, string> | null = null;
  private timelineGfxEditorBaselineInMs: number | null = null;
  private timelineGfxEditorBaselineOutMs: number | null = null;

  private readonly searchSubject = new Subject<string>();
  private readonly mamSearchSubject = new Subject<string>();
  private searchSub!: Subscription;
  private mamSearchSub!: Subscription;

  private readonly initialState = createInitialClippingStudioState();
  readonly state = toSignal(this.manager.state$, {
    initialValue: this.initialState as ClippingStudioState,
  });

  readonly selectedGraphic = computed(() => {
    const s = this.state();
    const id = s?.selectedGraphicId;
    if (!id) return null;
    const effective = this.manager.getEffectiveGraphics(
      s as ClippingStudioState,
      this.manager.currentPreviewDestinationId,
    );
    return effective.find((g) => g.id === id) ?? null;
  });

  readonly mamGraphicsTemplates = computed(() =>
    this.mamItems().filter((i) => i.kind === "gfx" && i.isTemplate),
  );

  /** Must read `state()` (signal), not `manager.state`, so this computed reacts to mode changes. */
  readonly isEditMode = computed(() => this.state().mode === "EDIT");

  readonly selectedGfxTimelineGraphic = computed((): ClippingStudioGraphicEvent | null => {
    const g = this.selectedGraphic();
    if (!g || g.type !== "gfx" || g.gfxInstanceId == null) return null;
    return g;
  });

  readonly activeGfxParamsGraphicsInfo = computed(() => {
    if (this.selectedMamInstanceForEdit()) {
      return this.selectedMamInstanceGraphicsInfo();
    }
    if (this.selectedGfxTimelineGraphic()) {
      return this.timelineGfxGraphicsInfo();
    }
    return null;
  });

  readonly activeGfxParamsLoading = computed(() => {
    if (this.selectedMamInstanceForEdit()) {
      return this.mamInstanceEditorLoading();
    }
    if (this.selectedGfxTimelineGraphic()) {
      return this.timelineGfxEditorLoading();
    }
    return false;
  });

  readonly activeGfxParamsError = computed((): string | null => {
    if (this.selectedMamInstanceForEdit()) {
      return this.mamInstanceEditorError();
    }
    if (this.selectedGfxTimelineGraphic()) {
      return this.timelineGfxEditorError();
    }
    return null;
  });

  readonly activeGfxParamsMaterializeReady = computed(() => {
    if (this.selectedMamInstanceForEdit()) {
      return this.mamInstanceMaterializeReady();
    }
    if (this.selectedGfxTimelineGraphic()) {
      return this.timelineGfxMaterializeReady();
    }
    return false;
  });

  readonly activeGfxParamsMaterializeLoading = computed(() => {
    if (this.selectedMamInstanceForEdit()) {
      return this.mamInstanceMaterializeLoading();
    }
    if (this.selectedGfxTimelineGraphic()) {
      return this.timelineGfxMaterializeLoading();
    }
    return false;
  });

  private readonly injector = inject(Injector);

  private openedGraphicId: string | null = null;
  private openedGraphicSnapshot: ClippingStudioGraphicEvent | null = null;

  constructor(
    private readonly graphicsRepository: IClippingStudioGraphicsRepository,
    private readonly mamCatalogService: IClippingStudioMamCatalogService,
    private readonly schedulerGraphicsRepository: GraphicsRepository,
    readonly manager: ClippingStudioManagerService,
    private readonly gfxManager: ClippingStudioGfxManagerService,
  ) {
    effect(
      () => {
        const graphic = this.selectedGfxTimelineGraphic();
        if (this.selectedMamInstanceForEdit()) {
          return;
        }
        if (!graphic) {
          this.loadedTimelineGfxId = null;
          untracked(() => this.resetTimelineGfxEditorSignals());
          return;
        }
        // Guard against re-running when only unrelated fields of the graphic
        // reference change (e.g. preview-only gfxParams updates during live scale).
        // Only load once per selected timeline GFX id.
        if (graphic.id === this.loadedTimelineGfxId) {
          return;
        }
        this.loadedTimelineGfxId = graphic.id;
        const seq = ++this.timelineGfxLoadSeq;
        untracked(() => {
          this.timelineGfxEditorLoading.set(true);
          this.timelineGfxEditorError.set(null);
          this.timelineGfxGraphicsInfo.set(null);
          this.timelineGfxMaterializeLoading.set(true);
          this.timelineGfxMaterializeReady.set(false);
          void this.loadTimelineGfxEditor(graphic, seq);
        });
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const g = this.selectedGraphic();
        if (!g) {
          this.openedGraphicId = null;
          this.openedGraphicSnapshot = null;
          return;
        }
        if (g.id !== this.openedGraphicId) {
          this.openedGraphicId = g.id;
          this.openedGraphicSnapshot = { ...g };
        }
      },
      { injector: this.injector },
    );
  }

  ngOnInit(): void {
    this.loadCatalog();

    this.searchSub = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => this.performSearch(query));

    this.mamSearchSub = this.mamSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => this.loadMamItemsForGraphics(query));
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.mamSearchSub?.unsubscribe();
  }

  onSearchTermChange(value: string): void {
    this.searchTerm.set(value);
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchTerm.set("");
    this.searchSubject.next("");
  }

  setSourceTab(tab: "elements" | "mam"): void {
    this.sourceTab.set(tab);
    this.closeMamInstanceEditor();
    if (tab === "mam") {
      this.loadMamItemsForGraphics(this.mamSearchTerm());
    }
  }

  onMamSearchTermChange(value: string): void {
    this.mamSearchTerm.set(value);
    this.mamSearchSubject.next(value);
  }

  clearMamSearch(): void {
    this.mamSearchTerm.set("");
    this.currentMamGraphicsTemplateIndex = null;
    this.mamSearchSubject.next("");
  }

  onMamInstancesClick(templateIndex: number | null): void {
    if (templateIndex === this.currentMamGraphicsTemplateIndex) {
      this.currentMamGraphicsTemplateIndex = null;
      return;
    }
    this.currentMamGraphicsTemplateIndex = templateIndex;
  }

  async openMamInstanceEditor(item: ClippingStudioMamItem): Promise<void> {
    this.manager.selectGraphic(null);
    this.selectedMamInstanceForEdit.set(item);
    this.mamInstanceEditorLoading.set(true);
    this.mamInstanceEditorError.set(null);
    this.selectedMamInstanceGraphicsInfo.set(null);
    this.mamInstanceMaterializeLoading.set(true);
    this.mamInstanceMaterializeReady.set(false);
    try {
      const details = await this.schedulerGraphicsRepository.getGraphics(
        item.mamGraphicsId,
      );
      this.selectedMamInstanceGraphicsInfo.set(details);

      // Start materialization immediately on INSTANCE click (async, deduped in manager)
      const res = await this.gfxManager.materializeInstance(item.mamGraphicsId);
      this.mamInstanceMaterializeReady.set(res.isSuccess && res.status === "ready");
      if (!res.isSuccess || res.status !== "ready") {
        console.error("[Graphics] materializeInstance failed:", res);
        this.mamInstanceEditorError.set(USER_FACING_ERROR_MESSAGE);
      }
    } catch (err) {
      console.error("[Graphics] failed to load graphic parameters:", err);
      this.mamInstanceEditorError.set(USER_FACING_ERROR_MESSAGE);
      this.selectedMamInstanceGraphicsInfo.set(null);
    } finally {
      this.mamInstanceEditorLoading.set(false);
      this.mamInstanceMaterializeLoading.set(false);
    }
  }

  closeMamInstanceEditor(): void {
    this.selectedMamInstanceForEdit.set(null);
    this.selectedMamInstanceGraphicsInfo.set(null);
    this.mamInstanceEditorError.set(null);
    this.mamInstanceEditorLoading.set(false);
    this.mamInstanceMaterializeLoading.set(false);
    this.mamInstanceMaterializeReady.set(false);
  }

  onGfxParamsFieldsChange(): void {
    if (this.selectedMamInstanceForEdit()) {
      const current = this.selectedMamInstanceGraphicsInfo();
      if (!current) return;
      const next = Object.assign(
        Object.create(Object.getPrototypeOf(current)),
        current,
      ) as GraphicsInfo;
      this.selectedMamInstanceGraphicsInfo.set(next);
      return;
    }
    const current = this.timelineGfxGraphicsInfo();
    if (!current) return;
    const next = Object.assign(
      Object.create(Object.getPrototypeOf(current)),
      current,
    ) as GraphicsInfo;
    this.timelineGfxGraphicsInfo.set(next);

    // Live preview for timeline GFX: resize the existing iframe immediately
    // and update project state preview-only (skipPersist/skipUndo) so Cancel
    // can revert via openedGraphicSnapshot. Save persists via the normal flow.
    const graphic = this.selectedGfxTimelineGraphic();
    if (!graphic) return;
    const gfxParams = this.buildGfxParamsFromGraphicsInfo(next);
    this.gfxManager.applyLiveScale(graphic.id, gfxParams);
    this.manager.updateGraphic(
      graphic.id,
      { gfxParams },
      { skipUndo: true, skipPersist: true },
    );
  }

  onCloseGfxParamsEditorShell(): void {
    if (this.selectedMamInstanceForEdit()) {
      this.closeMamInstanceEditor();
      return;
    }
    const graphic = this.selectedGfxTimelineGraphic();
    if (
      graphic &&
      this.openedGraphicSnapshot &&
      this.openedGraphicSnapshot.id === graphic.id
    ) {
      const snap = this.openedGraphicSnapshot;
      const revertParams = snap.gfxParams ?? {};
      this.manager.updateGraphic(
        graphic.id,
        { gfxParams: revertParams },
        { skipUndo: true, skipPersist: true },
      );
      this.gfxManager.applyLiveScale(graphic.id, revertParams);
    }
    this.openedGraphicId = null;
    this.openedGraphicSnapshot = null;
    this.manager.selectGraphic(null);
  }

  onSaveTimelineGfxChanges(): void {
    if (this.manager.state.mode !== "EDIT") return;
    const graphic = this.selectedGfxTimelineGraphic();
    const graphicsInfo = this.timelineGfxGraphicsInfo();
    if (!graphic || !graphicsInfo) return;
    if (graphic.gfxInstanceId == null) return;
    if (
      this.timelineGfxEditorLoading() ||
      this.timelineGfxMaterializeLoading() ||
      !this.timelineGfxMaterializeReady()
    ) {
      return;
    }
    const gfxParams = this.buildGfxParamsFromGraphicsInfo(graphicsInfo);
    this.updateTimelineGfxParamsRequested.emit({
      timelineGfxId: graphic.id,
      gfxInstanceId: graphic.gfxInstanceId,
      gfxParams,
      baselineGfxParams: this.timelineGfxEditorBaselineGfxParams ?? {},
      baselineInMs: this.timelineGfxEditorBaselineInMs ?? graphic.inMs,
      baselineOutMs: this.timelineGfxEditorBaselineOutMs ?? graphic.outMs,
      onComplete: (success) => {
        if (!success) return;
        this.openedGraphicId = null;
        this.openedGraphicSnapshot = null;
        this.manager.selectGraphic(null);
      },
    });
  }

  getCurrentMamGraphicsTemplate(): ClippingStudioMamItem | null {
    if (!Number.isInteger(this.currentMamGraphicsTemplateIndex)) {
      return null;
    }
    return this.mamGraphicsTemplates()[this.currentMamGraphicsTemplateIndex!] ?? null;
  }

  getCurrentMamGraphicsInstances(): ClippingStudioMamItem[] {
    const template = this.getCurrentMamGraphicsTemplate();
    if (!template?.mamGraphicsId) {
      return [];
    }
    return this.mamItems().filter(
      (i) =>
        i.kind === "gfx" &&
        !i.isTemplate &&
        i.instanceTemplateId === template.mamGraphicsId,
    );
  }

  onAddGraphic(item: ClippingStudioGraphicCatalogItem): void {
    if (this.manager.state.mode !== "EDIT") return;
    if (item.type === "gfx") return;
    this.manager.addGraphic(
      item as Omit<ClippingStudioGraphicCatalogItem, "type"> & {
        type: Exclude<GraphicType, "gfx">;
      },
    );
  }

  onAddGfxToTimeline(): void {
    if (this.manager.state.mode !== "EDIT") return;

    const instance = this.selectedMamInstanceForEdit();
    const graphicsInfo = this.selectedMamInstanceGraphicsInfo();
    if (!instance) return;
    if (!this.mamInstanceMaterializeReady()) return;

    const gfxInstanceId = instance.mamGraphicsId;
    const gfxParams = this.buildGfxParamsFromGraphicsInfo(graphicsInfo);
    const durationMs =
      graphicsInfo?.durationMS && graphicsInfo.durationMS > 0
        ? graphicsInfo.durationMS
        : 10_000;

    this.addGfxToTimeline.emit({ gfxInstanceId, gfxParams, durationMs });
    this.closeMamInstanceEditor();
  }

  onUpdateGraphic(changes: Partial<ClippingStudioGraphicEvent>): void {
    const graphic = this.selectedGraphic();
    if (!graphic) return;
    this.manager.updateGraphic(graphic.id, changes);
  }

  onPreviewGraphicChange(changes: Partial<ClippingStudioGraphicEvent>): void {
    const graphic = this.selectedGraphic();
    if (!graphic) return;
    this.manager.updateGraphic(graphic.id, changes, { skipUndo: true, skipPersist: true });
  }

  onCloseEditor(e?: { saved: boolean }): void {
    const graphic = this.selectedGraphic();
    const saved = e?.saved ?? true;
    if (graphic && saved === false && this.openedGraphicSnapshot && this.openedGraphicSnapshot.id === graphic.id) {
      const revert: Partial<ClippingStudioGraphicEvent> = { ...this.openedGraphicSnapshot };
      delete (revert as { id?: string }).id;
      this.manager.updateGraphic(graphic.id, revert, { skipUndo: true, skipPersist: true });
    }
    this.manager.selectGraphic(null);
    this.openedGraphicId = null;
    this.openedGraphicSnapshot = null;
  }

  private resetTimelineGfxEditorSignals(): void {
    this.timelineGfxGraphicsInfo.set(null);
    this.timelineGfxEditorError.set(null);
    this.timelineGfxEditorLoading.set(false);
    this.timelineGfxMaterializeLoading.set(false);
    this.timelineGfxMaterializeReady.set(false);
    this.timelineGfxEditorBaselineGfxParams = null;
    this.timelineGfxEditorBaselineInMs = null;
    this.timelineGfxEditorBaselineOutMs = null;
  }

  private async loadTimelineGfxEditor(
    graphic: ClippingStudioGraphicEvent,
    seq: number,
  ): Promise<void> {
    const id = graphic.gfxInstanceId!;
    try {
      const details = await this.schedulerGraphicsRepository.getGraphics(id);
      if (seq !== this.timelineGfxLoadSeq) {
        return;
      }
      const merged = this.applyGfxParamsToGraphicsInfo(
        details,
        graphic.gfxParams,
      );
      this.timelineGfxGraphicsInfo.set(merged);
      this.timelineGfxEditorBaselineGfxParams =
        this.buildGfxParamsFromGraphicsInfo(merged);
      const eff = this.manager.getEffectiveGraphics(
        this.manager.state,
        this.manager.currentPreviewDestinationId,
      );
      const seg = eff.find((g) => g.id === graphic.id && g.type === "gfx");
      if (seg) {
        this.timelineGfxEditorBaselineInMs = seg.inMs;
        this.timelineGfxEditorBaselineOutMs = seg.outMs;
      } else {
        this.timelineGfxEditorBaselineInMs = graphic.inMs;
        this.timelineGfxEditorBaselineOutMs = graphic.outMs;
      }

      const res = await this.gfxManager.materializeInstance(id);
      if (seq !== this.timelineGfxLoadSeq) {
        return;
      }
      this.timelineGfxMaterializeReady.set(res.isSuccess && res.status === "ready");
      if (!res.isSuccess || res.status !== "ready") {
        console.error("[Graphics] materializeInstance failed:", res);
        this.timelineGfxEditorError.set(USER_FACING_ERROR_MESSAGE);
      }
    } catch (err) {
      if (seq !== this.timelineGfxLoadSeq) {
        return;
      }
      console.error("[Graphics] failed to load graphic parameters:", err);
      this.timelineGfxEditorError.set(USER_FACING_ERROR_MESSAGE);
      this.timelineGfxGraphicsInfo.set(null);
    } finally {
      if (seq === this.timelineGfxLoadSeq) {
        this.timelineGfxEditorLoading.set(false);
        this.timelineGfxMaterializeLoading.set(false);
      }
    }
  }

  private buildGfxParamsFromGraphicsInfo(
    graphicsInfo: GraphicsInfo | null,
  ): Record<string, string> {
    const gfxParams: Record<string, string> = {};
    if (!graphicsInfo) {
      return gfxParams;
    }

    // Template-driven query params: use backend-provided field labels (e.g. TEXT1, TEXT2A, COLORZ, Stroke1, Scale).
    // Media params are intentionally not supported yet (excluded by design).
    for (const field of graphicsInfo.fields ?? []) {
      const key = (field?.label ?? "").trim();
      if (!key) continue;

      if (field instanceof GraphicsInfoTextField) {
        gfxParams[key] = field.text ?? "";
        continue;
      }
      if (field instanceof GraphicsInfoColorField) {
        const raw = (field as GraphicsInfoColorField).value as unknown;
        if (Array.isArray(raw)) {
          if (raw.length > 0 && raw[0] != null) gfxParams[key] = String(raw[0]);
        } else if (raw != null) {
          gfxParams[key] = String(raw);
        }
        continue;
      }
      if (field instanceof GraphicsInfoScaleField) {
        gfxParams[key] = String(field.value);
        continue;
      }
      if (field instanceof GraphicsInfoPositionField) {
        gfxParams[key] = String(field.value);
        continue;
      }
    }

    // Some templates (see QA GFX) expect duration from querystring under 'Duration' (ms).
    if (Number.isFinite(graphicsInfo.durationMS) && graphicsInfo.durationMS > 0) {
      gfxParams["Duration"] = String(Math.round(graphicsInfo.durationMS));
    }

    return gfxParams;
  }

  private applyGfxParamsToGraphicsInfo(
    base: GraphicsInfo,
    gfxParams: Record<string, string> | undefined,
  ): GraphicsInfo {
    const fields = base.fields?.map((f) => {
      const key = (f?.label ?? "").trim();
      if (f instanceof GraphicsInfoTextField) {
        const text = key && gfxParams != null && gfxParams[key] !== undefined ? gfxParams[key]! : f.text;
        return new GraphicsInfoTextField(
          f.id,
          f.label,
          text,
          f.link,
          f.fontId,
          f.size,
          f.color,
          f.align,
          f.dynamicOptions,
          f.shrinkToFit,
          f.isTicker,
          f.tickerSpeed,
          f.tickerDirection,
          f.tickerIsLoop,
        );
      }
      if (f instanceof GraphicsInfoColorField) {
        const next = key && gfxParams != null && gfxParams[key] !== undefined ? gfxParams[key]! : undefined;
        if (next === undefined) return f;
        return new GraphicsInfoColorField(f.id, f.label, [String(next)]);
      }
      if (f instanceof GraphicsInfoScaleField) {
        const next = key && gfxParams != null && gfxParams[key] !== undefined ? gfxParams[key]! : undefined;
        if (next === undefined) return f;
        const parsed = parseFloat(String(next));
        if (!Number.isFinite(parsed)) return f;
        return new GraphicsInfoScaleField(f.id, f.label, parsed);
      }
      if (f instanceof GraphicsInfoPositionField) {
        const next = key && gfxParams != null && gfxParams[key] !== undefined ? gfxParams[key]! : undefined;
        if (next === undefined) return f;
        const parsed = parseFloat(String(next));
        if (!Number.isFinite(parsed)) return f;
        return new GraphicsInfoPositionField(f.id, f.label, parsed);
      }
      return f;
    });

    const durationFromParamsRaw =
      gfxParams != null && gfxParams["Duration"] !== undefined ? gfxParams["Duration"] : undefined;
    const durationFromParams =
      durationFromParamsRaw != null ? parseFloat(String(durationFromParamsRaw)) : NaN;
    const nextDurationMS =
      Number.isFinite(durationFromParams) && durationFromParams > 0
        ? Math.round(durationFromParams)
        : base.durationMS;

    return new GraphicsInfo(
      base.isTemplate,
      base.id,
      base.uid,
      base.instanceTemplateId,
      base.name,
      base.description,
      base.thumbnail,
      base.sampleVideo,
      base.startLoopMS,
      base.endLoopMS,
      nextDurationMS,
      base.zLayer,
      fields,
      base.overrideGraphicPosition,
    );
  }

  private async loadCatalog(): Promise<void> {
    this.elementsCatalogLoading.set(true);
    this.elementsCatalogError.set(null);
    try {
      const items = await this.graphicsRepository.getCatalogItems();
      this.filteredItems.set(items);
    } catch (err) {
      this.elementsCatalogError.set(
        (err as Error)?.message ?? "Failed to load graphics catalog",
      );
      this.filteredItems.set([]);
    } finally {
      this.elementsCatalogLoading.set(false);
    }
  }

  private async performSearch(query: string): Promise<void> {
    if (!query.trim()) {
      await this.loadCatalog();
      return;
    }
    this.elementsCatalogLoading.set(true);
    this.elementsCatalogError.set(null);
    try {
      const items = await this.graphicsRepository.searchCatalogItems(query);
      this.filteredItems.set(items);
    } catch (err) {
      this.elementsCatalogError.set(
        (err as Error)?.message ?? "Failed to search graphics catalog",
      );
      this.filteredItems.set([]);
    } finally {
      this.elementsCatalogLoading.set(false);
    }
  }

  private async loadMamItemsForGraphics(searchQuery: string): Promise<void> {
    if (this.sourceTab() !== "mam") return;
    this.mamLoading.set(true);
    try {
      const items = await this.mamCatalogService.getMamItemsForGraphics(
        searchQuery,
      );
      this.mamItems.set(items);
      this.currentMamGraphicsTemplateIndex = null;
    } catch {
      this.mamItems.set([]);
      this.currentMamGraphicsTemplateIndex = null;
    } finally {
      this.mamLoading.set(false);
    }
  }
}
